// Lagerkorreksjon Service - Bruker eksisterende StcTransOrder endepunkter
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface InventoryCorrectionRequest {
  itemNo: string;
  locationName: string;
  quantity: number;
  warehouse: string;
  transType: number; // 1=ut av lager, 2=inn på lager
  reasonCode: number;
  description1?: string;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  itemType?: number;
}

export interface InventoryCorrectionLine {
  lineID: number;
  itemNo: string;
  locationName: string;
  quantity: number;
  warehouse: string;
  transType: number;
  transTypeText: string;
  reasonCode: number;
  description1: string;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  transDt: Date;
  lineStatus: number;
  statusText: string;
}

export interface InventoryCorrectionSession {
  batchID: string;
  operatorID: string;
  description1: string;
  orderStatus: number;
  statusText: string;
  warehouse: string;
  orderDate: Date;
  transactionCount: number;
  lines: InventoryCorrectionLine[];
}

export interface ReasonCode {
  code: number;
  description: string;
}

export interface ItemValidationResult {
  valid: boolean;
  itemNo: string;
  description1?: string;
  itemType?: number;
  errorMessage?: string;
}

export interface LocationValidationResult {
  valid: boolean;
  locationName: string;
  warehouseID?: string;
  errorMessage?: string;
}

// Interfaces for lagerbeholdning (samme som i warehouse transfer)
export interface ItemLocationInfo {
  itemNo: string;
  description: string;
  locationName: string;
  warehouseID: string;
  availableQuantity: number;
  totalQuantity: number;
  reservedQuantity: number;
  binCodeFixed: boolean;
  binCodePrimary: boolean;
  lastMovementDate?: Date;
}

export interface LocationItemInfo {
  locationName: string;
  warehouseID: string;
  itemNo: string;
  description: string;
  itemType?: number;
  availableQuantity: number;
  totalQuantity: number;
  reservedQuantity: number;
  binCodeFixed: boolean;
  binCodePrimary: boolean;
  lastMovementDate?: Date;
}

export interface InventoryCorrectionFormData {
  scannedCode: string;
  quantity: string;
  transType: number;
  reasonCode: number;
  isScanning: boolean;
  validationMessage: string;
  validationType: 'success' | 'error' | '';
}

class InventoryCorrectionService {
  private baseUrl = API_BASE_URL;

  // Hardkodede årsakskoder (kan utvides til API-call senere)
  private reasonCodes: ReasonCode[] = [
    { code: 1, description: 'Telleavvik' },
    { code: 2, description: 'Skade' },
    { code: 3, description: 'Svinn' },
    { code: 4, description: 'Systemfeil' },
    { code: 5, description: 'Manuell justering' },
    { code: 10, description: 'Annet' }
  ];

  // Få tilgjengelige årsakskoder
  getReasonCodes(): ReasonCode[] {
    return this.reasonCodes;
  }

  // Få aktiv korreksjons-session for operatør (top 1)
  async getActiveCorrectionSession(operatorId: string): Promise<InventoryCorrectionSession | null> {
    try {
      // Hent alle correction sessions for operatøren (OrderType 3 = inventory correction, Status 20 = Under arbeid)
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/operator/${operatorId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch correction sessions');
      }
      const sessions = await response.json();
      
      // Finn aktiv correction session (status 20 og OrderType 3)
      const activeSessions = sessions.filter((s: any) => 
        s.orderType === 3 && s.orderStatus === 20
      );
      
      if (activeSessions.length === 0) {
        return null;
      }

      // Ta den nyeste
      const activeSession = activeSessions.sort((a: any, b: any) => 
        new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
      )[0];

      // Hent detaljer med transakcsjoner
      const detailResponse = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/${activeSession.batchID}/details`);
      if (!detailResponse.ok) {
        throw new Error('Failed to fetch session details');
      }
      const details = await detailResponse.json();

      return this.mapToCorrectionSession(details);
    } catch (error) {
      console.error('Error getting active correction session:', error);
      return null;
    }
  }

  // Opprett ny korreksjons-session
  async createCorrectionSession(operatorId: string, warehouse: string): Promise<InventoryCorrectionSession> {
    try {
      const now = new Date();
      const batchID = `CORR_${now.toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14)}_${operatorId}`;
      
      const request = {
        batchID,
        operatorID: operatorId,
        orderDate: now,
        description1: `Lagerkorreksjon ${now.toLocaleDateString('no-NO')} ${now.toLocaleTimeString('no-NO')} ${operatorId}`,
        orderStatus: 20, // Under arbeid
        profileID: `20000477${operatorId}`, // Standard profil
        orderType: 3, // Inventory correction (ikke 2 som er warehouse transfer)
        warehouse,
        lineTypeOrigin: 0 // Standard
        // StatID genereres automatisk av backend (erstatter INSERT trigger)
      };

      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to create correction session');
      }
      
      // Return session med tomme linjer
      const responseData = await response.json();
      return {
        batchID: responseData.batchID,
        operatorID: responseData.operatorID,
        description1: responseData.description1,
        orderStatus: responseData.orderStatus,
        statusText: responseData.statusText,
        warehouse: responseData.warehouse,
        orderDate: new Date(responseData.orderDate),
        transactionCount: 0,
        lines: []
      };
    } catch (error) {
      console.error('Error creating correction session:', error);
      throw new Error('Kunne ikke opprette korreksjonssession');
    }
  }

  // Valider varenummer (samme som warehouse transfer)
  async validateItem(itemNo: string): Promise<ItemValidationResult> {
    try {
      console.log('🔍 [validateItem] Validating item:', itemNo);
      const startTime = performance.now();

      // Try barcode search first
      const barcodeResponse = await AuthService.authenticatedGet(`${this.baseUrl}/items/barcode/current?barcode=${encodeURIComponent(itemNo)}`);

      const barcodeEndTime = performance.now();
      console.log(`⏱️ [validateItem] Barcode search took ${Math.round(barcodeEndTime - startTime)}ms, status: ${barcodeResponse.status}`);

      if (barcodeResponse.ok) {
        const barcodeData = await barcodeResponse.json();
        if (barcodeData && barcodeData.length > 0) {
          const item = barcodeData[0];
          console.log('✅ [validateItem] Item found via barcode:', item.itemNo);
          return {
            valid: true,
            itemNo: item.itemNo,
            description1: item.description1,
            itemType: item.itemType
          };
        }
      }

      // Barcode didn't match - try direct item number search using OPTIMIZED search endpoint
      console.log('🔍 [validateItem] Barcode not found, trying direct item number search...');
      const itemSearchStart = performance.now();
      const itemResponse = await AuthService.authenticatedGet(`${this.baseUrl}/items/search/current?searchTerm=${encodeURIComponent(itemNo)}`);

      const itemSearchEnd = performance.now();
      console.log(`⏱️ [validateItem] Item search took ${Math.round(itemSearchEnd - itemSearchStart)}ms, status: ${itemResponse.status}`);

      if (!itemResponse.ok) {
        return {
          valid: false,
          itemNo,
          errorMessage: `Vare "${itemNo}" ble ikke funnet`
        };
      }

      const itemData = await itemResponse.json();
      if (itemData && itemData.length > 0) {
        // Find exact match if exists, otherwise take first result
        const exactMatch = itemData.find((i: any) => i.itemNo.toLowerCase() === itemNo.toLowerCase());
        const item = exactMatch || itemData[0];
        console.log('✅ [validateItem] Item found via item search:', item.itemNo);
        return {
          valid: true,
          itemNo: item.itemNo,
          description1: item.description1,
          itemType: item.itemType
        };
      }

      console.log('❌ [validateItem] Item not found');
      return {
        valid: false,
        itemNo,
        errorMessage: `Vare "${itemNo}" ble ikke funnet`
      };
    } catch (error) {
      console.error('❌ [validateItem] Error validating item:', error);
      return {
        valid: false,
        itemNo,
        errorMessage: 'Feil ved validering av vare'
      };
    }
  }

  // Valider lokasjon (samme som warehouse transfer)
  async validateLocation(locationName: string): Promise<LocationValidationResult> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stc-bins/current`);
      if (!response.ok) {
        throw new Error('Failed to validate location');
      }
      const bins = await response.json();
      
      const foundBin = bins.find((bin: any) => bin.binCode === locationName);
      
      if (foundBin) {
        return {
          valid: true,
          locationName: foundBin.binCode,
          warehouseID: foundBin.warehouseID
        };
      }

      return {
        valid: false,
        locationName,
        errorMessage: `Lokasjon "${locationName}" ble ikke funnet`
      };
    } catch (error) {
      console.error('Error validating location:', error);
      return {
        valid: false,
        locationName,
        errorMessage: 'Feil ved validering av lokasjon'
      };
    }
  }

  // Hent lokasjoner for en vare (samme som warehouse transfer)
  async getLocationsForItem(itemNo: string): Promise<ItemLocationInfo[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/data-inspection/itembins/item/${encodeURIComponent(itemNo)}`);
      if (!response.ok) {
        throw new Error('Failed to get locations for item');
      }
      
      const itemBins = await response.json();

      // Aggreger per BinCode+WarehouseID — batch-varer har flere ItemBins-rader per lokasjon
      const locationMap = new Map<string, ItemLocationInfo>();
      for (const bin of itemBins) {
        const key = `${bin.binCode}|${bin.warehouseID}`;
        const existing = locationMap.get(key);
        if (existing) {
          existing.availableQuantity += (bin.quantityAvailable || 0);
          existing.totalQuantity += (bin.quantity || 0);
          existing.reservedQuantity += (bin.quantityReserved || 0);
          if (bin.binCodePrimary) existing.binCodePrimary = true;
          if (bin.binCodeFixed) existing.binCodeFixed = true;
          if (bin.lastMovementDate) {
            const newDate = new Date(bin.lastMovementDate);
            if (!existing.lastMovementDate || newDate > existing.lastMovementDate) {
              existing.lastMovementDate = newDate;
            }
          }
        } else {
          locationMap.set(key, {
            itemNo: bin.itemNo,
            description: bin.itemDescription || '',
            locationName: bin.binCode,
            warehouseID: bin.warehouseID,
            availableQuantity: bin.quantityAvailable || 0,
            totalQuantity: bin.quantity || 0,
            reservedQuantity: bin.quantityReserved || 0,
            binCodeFixed: bin.binCodeFixed || false,
            binCodePrimary: bin.binCodePrimary || false,
            lastMovementDate: bin.lastMovementDate ? new Date(bin.lastMovementDate) : undefined
          });
        }
      }
      return Array.from(locationMap.values()); // Ikke filtrer på tilgjengelig antall for korreksjon - kan korrigere negative beholdninger også
    } catch (error) {
      console.error('Error getting locations for item:', error);
      return [];
    }
  }

  // Hent varer på en lokasjon (samme som warehouse transfer)
  async getItemsAtLocation(locationName: string): Promise<LocationItemInfo[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/data-inspection/itembins/bin/${encodeURIComponent(locationName)}`);
      if (!response.ok) {
        throw new Error('Failed to get items at location');
      }
      
      const itemBins = await response.json();
      
      return itemBins.map((bin: any): LocationItemInfo => ({
        locationName: bin.binCode,
        warehouseID: bin.warehouseID,
        itemNo: bin.itemNo,
        description: bin.itemDescription || '',
        itemType: bin.itemType,
        availableQuantity: bin.quantityAvailable || 0,
        totalQuantity: bin.quantity || 0,
        reservedQuantity: bin.quantityReserved || 0,
        binCodeFixed: bin.binCodeFixed || false,
        binCodePrimary: bin.binCodePrimary || false,
        lastMovementDate: bin.lastMovementDate ? new Date(bin.lastMovementDate) : undefined
      })); // Ikke filtrer på tilgjengelig antall for korreksjon
    } catch (error) {
      console.error('Error getting items at location:', error);
      return [];
    }
  }

  // Legg til korreksjonslinje
  async addCorrectionLine(batchId: string, correctionData: InventoryCorrectionRequest): Promise<InventoryCorrectionLine> {
    try {
      // Hent operatorID fra batchID (format: "CORR_YYYYMMDDHHMMSS_OperatorID")
      const operatorID = batchId.split('_').slice(2).join('_');
      
      const request = {
        batchID: batchId,
        lineType: 3, // Lagerkorreksjon (legacy: LineType 3)
        orderNo: 0,
        lineNo: 0,
        locationName: correctionData.locationName, // Korreksjons-lokasjon
        quantity: correctionData.quantity,
        transDt: new Date(),
        toLocationName: null, // Ingen til-lokasjon for korreksjon
        warehouse: correctionData.warehouse,
        toWarehouse: correctionData.warehouse, // Samme warehouse
        operatorID: operatorID,
        profileID: `20000477${operatorID}`, // Samme som ved opprettelse av session
        exportedYesNo: false,
        lineStatus: 20, // Under arbeid
        itemNo: correctionData.itemNo,
        itemUnitCode: '',
        itemBarCode: null,
        description1: correctionData.description1 || '',
        itemBatchNo: correctionData.itemBatchNo || '',
        itemBatchExpDate: correctionData.itemBatchExpDate,
        itemSerialNo: correctionData.itemSerialNo || '',
        erpLinkName: '',
        shipmentNo: '1', // Standard shipmentNo
        quantityOrigin: 0,
        itemUnitTxt: '',
        lineTypeOrigin: 0, // Standard
        price: 0,
        currency: '',
        reasonCode: correctionData.reasonCode, // VIKTIG: Årsakskode
        transType: correctionData.transType, // VIKTIG: 1=ut av lager, 2=inn på lager
        sscc: '',
        quantityCorrected: correctionData.quantity, // Korrigert antall
        freeField1: null,
        freeField2: null,
        freeField3: null,
        appFreeField1: null,
        appFreeField2: null,
        quantityGross: 0,
        transitPackagingID: 0,
        stcBoxNo: 0,
        grShipmentNo: 0,
        externalSystemID: null,
        externalSystemStatus: null,
        itemType: correctionData.itemType ?? 0,
        orderNoERP: null,
        lineNoERP: 0
      };

      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans/${batchId}/transactions`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to add correction line');
      }
      
      const responseData = await response.json();
      return this.mapToCorrectionLine(responseData);
    } catch (error) {
      console.error('Error adding correction line:', error);
      throw new Error('Kunne ikke legge til korreksjonslinje');
    }
  }

  // Hent korreksjonslinjer for session
  async getCorrectionLines(batchId: string): Promise<InventoryCorrectionLine[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/${batchId}/transactions`);
      if (!response.ok) {
        throw new Error('Failed to fetch correction lines');
      }
      const transactions = await response.json();
      
      return transactions
        .filter((t: any) => t.lineType === 3) // Korreksjon (legacy: LineType 3)
        .map((t: any) => this.mapToCorrectionLine(t))
        .sort((a: any, b: any) => new Date(b.transDt).getTime() - new Date(a.transDt).getTime());
    } catch (error) {
      console.error('Error getting correction lines:', error);
      return [];
    }
  }

  // Fullfør korreksjonssession
  async completeCorrectionSession(batchId: string): Promise<boolean> {
    try {
      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans/${batchId}/complete`);
      if (!response.ok) {
        throw new Error('Failed to complete correction session');
      }
      return true;
    } catch (error) {
      console.error('Error completing correction session:', error);
      return false;
    }
  }

  // Oppdater korreksjonslinje
  async updateCorrectionLine(batchId: string, lineId: number, quantity: number): Promise<InventoryCorrectionLine> {
    try {
      const request = {
        quantity: quantity,
        quantityCorrected: quantity // Oppdater korrigert antall også
      };

      const response = await AuthService.authenticatedPut(`${this.baseUrl}/stocktrans/${batchId}/transactions/${lineId}`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to update correction line');
      }
      
      const responseData = await response.json();
      return this.mapToCorrectionLine(responseData);
    } catch (error) {
      console.error('Error updating correction line:', error);
      throw new Error('Kunne ikke oppdatere korreksjonslinje');
    }
  }

  // Slett korreksjonslinje
  async deleteCorrectionLine(batchId: string, lineId: number): Promise<boolean> {
    try {
      const response = await AuthService.authenticatedDelete(`${this.baseUrl}/stocktrans/${batchId}/transactions/${lineId}`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to delete correction line');
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting correction line:', error);
      throw new Error('Kunne ikke slette korreksjonslinje');
    }
  }

  // Helper methods
  private mapToCorrectionSession(details: any): InventoryCorrectionSession {
    return {
      batchID: details.header.batchID,
      operatorID: details.header.operatorID,
      description1: details.header.description1,
      orderStatus: details.header.orderStatus,
      statusText: details.header.statusText,
      warehouse: details.header.warehouse,
      orderDate: new Date(details.header.orderDate),
      transactionCount: details.totalTransactions,
      lines: details.transactions
        .filter((t: any) => t.lineType === 3) // Korreksjon (legacy: LineType 3)
        .map((t: any) => this.mapToCorrectionLine(t))
        .sort((a: any, b: any) => new Date(b.transDt).getTime() - new Date(a.transDt).getTime())
    };
  }

  private mapToCorrectionLine(transaction: any): InventoryCorrectionLine {
    return {
      lineID: transaction.lineID,
      itemNo: transaction.itemNo,
      locationName: transaction.locationName,
      quantity: transaction.quantity,
      warehouse: transaction.warehouse,
      transType: transaction.transType || 1,
      transTypeText: this.getTransTypeText(transaction.transType || 1),
      reasonCode: transaction.reasonCode || 1,
      description1: transaction.description1,
      itemBatchNo: transaction.itemBatchNo,
      itemBatchExpDate: transaction.itemBatchExpDate ? new Date(transaction.itemBatchExpDate) : undefined,
      itemSerialNo: transaction.itemSerialNo,
      transDt: new Date(transaction.transDt),
      lineStatus: transaction.lineStatus,
      statusText: transaction.statusText || 'Under arbeid'
    };
  }

  private getTransTypeText(transType: number): string {
    return transType === 1 ? 'Ut av lager' : 'Inn på lager';
  }

  // Få beskrivelse for årsakskode
  getReasonCodeDescription(reasonCode: number): string {
    const reason = this.reasonCodes.find(r => r.code === reasonCode);
    return reason ? reason.description : `Kode ${reasonCode}`;
  }
}

export const inventoryCorrectionService = new InventoryCorrectionService();
